export default function initTrustedPlatforms() {
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  document.querySelectorAll("[data-trusted-platforms]").forEach((block, blockIndex) => {
    const visual = block.querySelector(".trusted-platforms_visual");
    const source = block.querySelector("[data-signal-source]");
    const routes = Array.from(block.querySelectorAll("[data-signal-route]")).sort(
      (a, b) => Number(a.dataset.signalRoute) - Number(b.dataset.signalRoute),
    );
    const targets = Array.from(block.querySelectorAll("[data-signal-target]"));
    if (!visual || !source || !routes.length || !targets.length || reducedMotion) return;

    const svg = block.querySelector("svg");
    const defs = svg?.querySelector("defs");
    const svgNamespace = "http://www.w3.org/2000/svg";
    const opacityAt = (position) => {
      const stops = [
        [0, 0],
        [0.2, 0.45],
        [0.4, 1],
        [0.6, 1],
        [0.8, 0.45],
        [1, 0],
      ];
      const nextIndex = stops.findIndex(([offset]) => offset >= position);
      if (nextIndex <= 0) return stops[0][1];
      const [startOffset, startOpacity] = stops[nextIndex - 1];
      const [endOffset, endOpacity] = stops[nextIndex];
      const progress = (position - startOffset) / (endOffset - startOffset);
      return startOpacity + (endOpacity - startOpacity) * progress;
    };
    const segmentCount = 32;
    const createSignalSegments = (route, routeIndex, signalIndex) => {
      const group = document.createElementNS(svgNamespace, "g");
      group.classList.add("trusted-platforms_signal-gradient");
      group.style.filter = "drop-shadow(0 0 2px rgba(82, 71, 250, 1))";
      route.before(group);

      return Array.from({ length: segmentCount }, (_, segmentIndex) => {
        const line = document.createElementNS(svgNamespace, "line");
        line.setAttribute("stroke-width", "1.6");
        line.setAttribute("stroke-opacity", "1");
        line.setAttribute("stroke-linecap", "butt");
        line.setAttribute("shape-rendering", "geometricPrecision");
        line.setAttribute("vector-effect", "non-scaling-stroke");
        line.style.filter = "none";
        line.style.mixBlendMode = "normal";
        line.style.opacity = 0;
        const gradient = document.createElementNS(svgNamespace, "linearGradient");
        gradient.id = `network-signal-${blockIndex}-${routeIndex}-${signalIndex}-${segmentIndex}`;
        gradient.setAttribute("gradientUnits", "userSpaceOnUse");
        const startStop = document.createElementNS(svgNamespace, "stop");
        const endStop = document.createElementNS(svgNamespace, "stop");
        startStop.setAttribute("offset", "0");
        endStop.setAttribute("offset", "1");
        startStop.setAttribute("stop-color", "white");
        endStop.setAttribute("stop-color", "white");
        startStop.setAttribute("stop-opacity", opacityAt(segmentIndex / segmentCount));
        endStop.setAttribute("stop-opacity", opacityAt((segmentIndex + 1) / segmentCount));
        gradient.append(startStop, endStop);
        defs?.append(gradient);
        line.setAttribute("stroke", `url(#${gradient.id})`);
        group.append(line);
        return { line, gradient };
      });
    };

    routes.forEach((route, routeIndex) => {
      route.style.display = "none";
      route.signalSegments = [];
    });

    const route = routes[0];
    const trackId = route.getAttribute("href")?.replace("#", "");
    const track = trackId ? block.querySelector(`#${trackId}`) : null;
    if (!track || typeof track.getTotalLength !== "function") return;

    const length = track.getTotalLength();
    const segmentLength = 96;
    const duration = (length / 120) * 1000;
    const launchInterval = 3000;
    const signalCount = Math.ceil(duration / launchInterval) + 1;
    const signalPeriod = signalCount * launchInterval;
    route.signalSegments = Array.from({ length: signalCount }, (_, signalIndex) =>
      createSignalSegments(route, 0, signalIndex),
    );
    let running = false;
    let frameId = null;
    let startedAt = null;

    const hideSignal = (segments) => {
      segments.forEach(({ line }) => {
        line.style.opacity = 0;
      });
    };

    const drawSignal = (segments, center, opacity) => {
      const tail = center - segmentLength / 2;
      const head = center + segmentLength / 2;

      segments.forEach(({ line, gradient }, segmentIndex) => {
        const fromRatio = segmentIndex / segmentCount;
        const toRatio = (segmentIndex + 1) / segmentCount;
        const rawFromDistance = tail + (head - tail) * fromRatio;
        const rawToDistance = tail + (head - tail) * toRatio;
        const fullyOutside =
          (rawFromDistance < 0 && rawToDistance < 0) || (rawFromDistance > length && rawToDistance > length);
        if (fullyOutside) {
          line.style.opacity = 0;
          return;
        }
        const fromDistance = Math.max(0, Math.min(length, rawFromDistance));
        const toDistance = Math.max(0, Math.min(length, rawToDistance));
        const from = track.getPointAtLength(fromDistance);
        const to = track.getPointAtLength(toDistance);
        line.setAttribute("x1", from.x);
        line.setAttribute("y1", from.y);
        line.setAttribute("x2", to.x);
        line.setAttribute("y2", to.y);
        gradient.setAttribute("x1", from.x);
        gradient.setAttribute("y1", from.y);
        gradient.setAttribute("x2", to.x);
        gradient.setAttribute("y2", to.y);
        line.style.opacity = opacity;
      });
    };

    const draw = (timestamp) => {
      if (!running) return;
      if (startedAt === null) startedAt = timestamp;
      const elapsed = timestamp - startedAt;

      route.signalSegments.forEach((segments, signalIndex) => {
        const signalElapsed = (elapsed - signalIndex * launchInterval + signalPeriod) % signalPeriod;
        if (signalElapsed < 0) {
          hideSignal(segments);
          return;
        }
        if (elapsed < signalIndex * launchInterval || signalElapsed > duration) {
          hideSignal(segments);
          return;
        }

        const progress = signalElapsed / duration;
        const entryOpacity = Math.min(1, signalElapsed / 260);
        const exitOpacity = Math.min(1, (duration - signalElapsed) / 260);
        drawSignal(segments, length * progress, Math.min(entryOpacity, exitOpacity));
      });

      frameId = window.requestAnimationFrame(draw);
    };

    const run = () => {
      if (running) return;
      running = true;
      startedAt = null;
      frameId = window.requestAnimationFrame(draw);
    };

    const stop = () => {
      running = false;
      if (frameId !== null) window.cancelAnimationFrame(frameId);
      frameId = null;
      startedAt = null;
      route.signalSegments.forEach(hideSignal);
    };

    if (!("IntersectionObserver" in window)) {
      run();
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          run();
        } else stop();
      },
      { threshold: 0.12 },
    );

    observer.observe(block);
  });
}
